import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { Formats, format } from '@toasttab/buffet-pui-date-utilities'
import { CalendarTodayIcon, ScheduleIcon } from '@toasttab/buffet-pui-icons'
import { useIntlProps } from 'banquet-runtime-modules'
import { Reservation } from '../../api/bookings/getBooking'
import { RestaurantInfo } from '../../api/restaurant/getRestaurant'
import { useFindReserveContext } from '../../contexts/find-reserve-context'
import { useRestaurantContext } from '../../contexts/RestaurantContext'
import { useGetExperienceBySlug } from '../../api/experiences/useGetExperience'
import { Ticket } from '../ExperienceView/icons/Ticket'
import { PaymentType } from '../../api/payment/model'
import { useFormatCurrency } from '../../hooks/useFormatCurrency'
import { getPaymentLabelByStrategyAndType } from '../../utils/getPaymentLabelByStrategyAndType'

interface ReservationDetailParams {
  restaurant: RestaurantInfo
  booking?: Reservation
}

export const ReservationDetails = ({
  restaurant,
  booking
}: ReservationDetailParams) => {
  const formatCurrency = useFormatCurrency()
  const { selectedAvailability: contextualAvailability } =
    useFindReserveContext()
  const selectedAvailability = booking || contextualAvailability
  const { language: locale } = useIntlProps()
  const { shortUrl } = useRestaurantContext()
  const { experienceSlug } = useParams()
  const { data: experience } = useGetExperienceBySlug({
    experienceSlug: experienceSlug
  })
  const bookableName = experience ? experience.name : booking?.bookable?.name

  const paymentType = experience
    ? experience?.payment?.type
    : booking?.paymentConfigSnapshot?.type ||
      contextualAvailability?.payment?.paymentConfigType

  const paymentStrategy = experience
    ? experience.payment?.strategy
    : booking?.paymentConfigSnapshot?.strategy.type ||
      contextualAvailability?.payment?.strategy.type

  const strategyAmount = experience
    ? experience.payment?.strategyAmount
    : booking?.paymentConfigSnapshot?.strategy.strategyAmount ||
      contextualAvailability?.payment?.strategy.strategyAmount

  const formattedAmount = strategyAmount
    ? formatCurrency(strategyAmount, {
        trailingZeros: false
      })
    : null

  const paymentLabel =
    paymentType && paymentStrategy && formattedAmount
      ? getPaymentLabelByStrategyAndType(
          formattedAmount,
          paymentType,
          paymentStrategy,
          'per'
        )
      : null

  return (
    <div className='flex flex-col gap-2'>
      {bookableName ? (
        <>
          <p className='type-large font-semibold'>{bookableName}</p>
          <p>
            By{' '}
            <Link
              to={`/${shortUrl}/findTime`}
              className='underline hover:no-underline'
            >
              {restaurant.name}
            </Link>
          </p>
        </>
      ) : (
        <h3 className='type-large font-semibold'>{restaurant.name}</h3>
      )}
      <p className='font-semibold'>
        Table for {selectedAvailability?.partySize} in{' '}
        {booking?.serviceAreaGroup.name ||
          contextualAvailability?.serviceAreaGroupName}
        .
      </p>
      <div className='flex flex-col gap-2'>
        <div className='flex'>
          <CalendarTodayIcon
            size='sm'
            className='mr-1'
            accessibility='decorative'
          />
          {format(
            new Date(
              booking?.expectedStartTime || contextualAvailability!.datetime
            ),
            Formats.date.long,
            { locale: locale, timeZone: restaurant.timezone }
          )}{' '}
        </div>
        <div className='flex'>
          <ScheduleIcon size='sm' className='mr-1' accessibility='decorative' />
          {format(
            new Date(
              booking?.expectedStartTime || contextualAvailability!.datetime
            ),
            Formats.time.short,
            { locale: locale, timeZone: restaurant.timezone }
          )}
        </div>
        {paymentLabel && paymentType === PaymentType.PREPAYMENT && (
          <div className='flex'>
            <Ticket width={24} height={24} />
            <span className='ml-1'>{paymentLabel}</span>
          </div>
        )}
      </div>
    </div>
  )
}
